import { debounce } from 'lodash'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useIsMobileView } from '../../../hooks'
import styles from './_card-container.module.scss'

export type HandleCheckType = (
  /** Index of the card */
  index: number,
  /** Event object */
  event: React.MouseEvent<HTMLButtonElement>,
  /** Boolean value of whether the checkbox is checked */
  checked: boolean,
) => void

export type CheckedItemsType = { [key: number]: boolean }

export type CardContainerProps = {
  /** a function that passes the card width to each card in the children passed in */
  children: (
    cardWidth: number,
    handleCheck?: HandleCheckType,
    checkedItems?: CheckedItemsType,
    setCheckedItems?: React.Dispatch<
      React.SetStateAction<Record<number, boolean>>
    >,
  ) => React.ReactNode
}

const CardContainer = ({ children }: CardContainerProps) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [totalWidth, setTotalWidth] = useState(0)
  const isMobile = useIsMobileView()

  const [checkedItems, setCheckedItems] = useState<Record<number, boolean>>({})
  const [lastCheckedIndex, setLastCheckedIndex] = useState<number | null>(null)

  const handleCheck: HandleCheckType = (index, event, checked) => {
    setCheckedItems((prevCheckedItems) => {
      const newCheckedItems = { ...prevCheckedItems }

      if (event.shiftKey && lastCheckedIndex !== null) {
        // Determine the range between last clicked and current checkbox
        const start = Math.min(lastCheckedIndex, index)
        const end = Math.max(lastCheckedIndex, index)

        for (let i = start; i <= end; i++) {
          newCheckedItems[i] = checked
        }
      } else {
        newCheckedItems[index] = checked
      }

      return newCheckedItems
    })

    setLastCheckedIndex(index)
  }

  // These should match the card component styles in _card.module.scss. If those styles change, these need to be updated as well.
  const minWidth = 175
  const maxWidth = 400
  const border = 2 // 1px border on each side of the card

  const gap = isMobile ? 8 : 16

  const hasOneCardPerRow = useMemo(
    () => totalWidth < 2 * minWidth + gap + border,
    [gap, totalWidth],
  )

  const cardWidth = useMemo(() => {
    if (hasOneCardPerRow) {
      return totalWidth - border
    }

    const maxNumCards = Math.floor(
      (totalWidth + gap) / (minWidth + gap + border),
    )

    const adjustedWidth =
      totalWidth - (maxNumCards - 1) * gap - border * maxNumCards
    const cardWidth = adjustedWidth / maxNumCards

    return Math.min(cardWidth, maxWidth)
  }, [gap, hasOneCardPerRow, totalWidth])

  const handleResize = useCallback(() => {
    if (containerRef.current) {
      setTotalWidth(containerRef.current.offsetWidth)
    }
  }, [])

  useEffect(() => {
    const debouncedHandleResize = debounce(handleResize, 50)
    debouncedHandleResize()
    window.addEventListener('resize', debouncedHandleResize)
    return () => {
      window.removeEventListener('resize', debouncedHandleResize)
      debouncedHandleResize.cancel()
    }
  }, [handleResize])

  return (
    <div
      ref={containerRef}
      className={`${styles.cardContainer} ${hasOneCardPerRow ? styles.justifyCenter : ''}`}
      style={{ gap }}
    >
      {children(cardWidth, handleCheck, checkedItems, setCheckedItems)}
    </div>
  )
}

export default CardContainer
